import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from 'primeng/api';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { IndividualReportsService } from '../services/individual-reports.service';
import { IndTemplateBuilderActions } from './indTemplateBuilder.actions';

@Injectable()
export class IndTemplateBuilderEffects {
  loadTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IndTemplateBuilderActions.loadTemplate),
      // switchMap: a newer load-by-id cancels the superseded in-flight request
      switchMap(({ templateOid }) =>
        this.individualReportsService.getIndividualReportTemplate(templateOid).pipe(
          map((response) => {
            if (!response.success) {
              return IndTemplateBuilderActions.loadTemplateFailure({
                error: response.errorText || 'Failed to load template',
              });
            }
            return IndTemplateBuilderActions.loadTemplateSuccess({ template: response.data?.[0] ?? null });
          }),
          catchError((error: Error) =>
            of(IndTemplateBuilderActions.loadTemplateFailure({ error: error.message || 'Failed to load template' })),
          ),
        ),
      ),
    ),
  );

  saveTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IndTemplateBuilderActions.saveTemplate),
      mergeMap(({ templateOid, template, redirectToList }) =>
        this.individualReportsService.updateIndividualReportTemplate(templateOid, template).pipe(
          map((response) => {
            if (!response.success) {
              return IndTemplateBuilderActions.saveTemplateFailure({
                error: response.errorText || 'Failed to save template',
              });
            }
            return IndTemplateBuilderActions.saveTemplateSuccess({ redirectToList });
          }),
          catchError((error: Error) =>
            of(IndTemplateBuilderActions.saveTemplateFailure({ error: error.message || 'Failed to save template' })),
          ),
        ),
      ),
    ),
  );

  saveTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IndTemplateBuilderActions.saveTemplateSuccess),
        tap(({ redirectToList }) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Template saved',
            detail: 'The individual report template was saved.',
          });
          if (redirectToList) {
            this.router.navigate(['/individual-report-templates']);
          }
        }),
      ),
    { dispatch: false },
  );

  failures$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IndTemplateBuilderActions.loadTemplateFailure, IndTemplateBuilderActions.saveTemplateFailure),
        tap(({ error }) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: error });
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private individualReportsService: IndividualReportsService,
    private messageService: MessageService,
    private router: Router,
  ) {}
}
